import { Button, Link, Text } from "@growtherapy/sprout-ui";
import { useTranslate } from "@growtherapy/i18n";
import { Translate } from "../../i18n/Translate";
import { useAtomValue } from "jotai";
import { useVideoPlatform } from "../../hooks/useVideoPlatform";
import { AudioControlWrapper as AudioControl } from "../../session/controls/AudioControl";
import { VideoControlWrapper as VideoControl } from "../../session/controls/VideoControl";
import {
  LocalMediaStatus,
  VideoPlatform,
  localAudioContextAtom,
  localVideoContextAtom,
} from "../../video-sdk";
import { permissionDeniedAtom } from "../../video-sdk/twilio";
import { BlockedPermissionsActionItemWrapper as BlockedPermissionsActionItem } from "./BlockedPermissionsActionItem";
import { HelpModalItem } from "./HelpModalItem";

const CLIENT_AUDIO_VIDEO_TROUBLESHOOT_ARTICLE =
  "https://help.growtherapy.com/clients/en/articles/8482385-grow-therapy-telehealth-faq#h_f12c851172";

type AudioVideoTroubleshootActionItemProps = {
  permissionIsBlocked: boolean;
  isVideoEnabled?: boolean;
  isAudioEnabled?: boolean;
  onOpenSettings?: () => void;
  shouldShowVideoQualitySettings?: boolean;
};

export function AudioVideoTroubleshootActionItem({
  permissionIsBlocked,
  isVideoEnabled,
  isAudioEnabled,
  onOpenSettings,
  shouldShowVideoQualitySettings = true,
}: AudioVideoTroubleshootActionItemProps) {
  const t = useTranslate();

  if (permissionIsBlocked) {
    return <BlockedPermissionsActionItem />;
  }

  return (
    <>
      <HelpModalItem
        heading={t(
          "telehealth-core:troubleshooting.myCameraAndMicArentWorking",
        )}
        description={
          <div className="flex flex-col gap-4">
            <Text className="text-neutral-800">
              {t(
                "telehealth-core:troubleshooting.makeSureYourCameraAndMicrophoneAreOn",
              )}
            </Text>
            <div className="grid md:grid-cols-3 grid-cols-2">
              <div className="flex flex-row items-center gap-4">
                <VideoControl />
                <Text>
                  {isVideoEnabled
                    ? t("telehealth-core:troubleshooting.cameraOn")
                    : t("telehealth-core:troubleshooting.cameraOff")}
                </Text>
              </div>
              <div className="flex flex-row items-center gap-4">
                <AudioControl />
                {isAudioEnabled
                  ? t("telehealth-core:troubleshooting.microphoneOn")
                  : t("telehealth-core:troubleshooting.microphoneOff")}
              </div>
            </div>
            <Text variant="xs" className="text-neutral-700">
              <Translate
                i18nKey="telehealth-core:troubleshooting.ifYoureExperiencingCameraOrMicrophoneIssues"
                components={{
                  1: (
                    <Link
                      external
                      href={CLIENT_AUDIO_VIDEO_TROUBLESHOOT_ARTICLE}
                      className="text-neutral-700 hover:text-neutral-900 text-xsmall font-normal"
                    />
                  ),
                }}
              />
            </Text>
          </div>
        }
      />
      <HelpModalItem
        heading={t(
          "telehealth-core:troubleshooting.myVideoOrAudioQualityIsPoor",
        )}
        description={
          <>
            {t(
              "telehealth-core:troubleshooting.connectivityIssuesMayAffectYourVideoOrAudioQuality",
            )}
            <ul className="list-disc ml-4">
              <li>
                {t(
                  "telehealth-core:troubleshooting.turnYourVideoBackgroundOffIfEnabled",
                )}
              </li>
              <li>{t("telehealth-core:troubleshooting.turnYourCameraOff")}</li>
              {shouldShowVideoQualitySettings && (
                <li>
                  <Translate
                    i18nKey="telehealth-core:troubleshooting.inSettingsChangeYourVideoQualityToOptimizeForPerformance"
                    components={{
                      1: (
                        <Button
                          inline
                          className="font-normal text-inherit"
                          use="link"
                          onClick={() => {
                            onOpenSettings?.();
                          }}
                        />
                      ),
                    }}
                  />
                </li>
              )}
              <li>
                {t("telehealth-core:troubleshooting.getCloserToTheWifiRouter")}
              </li>
              <li>
                {t(
                  "telehealth-core:troubleshooting.closeBrowserTabsThatArentInUse",
                )}
              </li>
              <li>
                {t(
                  "telehealth-core:troubleshooting.ensureYourDeviceHasAGoodBatteryLevel",
                )}
              </li>
            </ul>
          </>
        }
      />
    </>
  );
}

export function AudioVideoTroubleshootActionItemWrapper(
  props: Pick<AudioVideoTroubleshootActionItemProps, "onOpenSettings">,
) {
  const blockedPermissions = useAtomValue(permissionDeniedAtom);
  const { videoIsBlocked, microphoneIsBlocked } = blockedPermissions;
  const videoPlatformIsZoom = useVideoPlatform() === VideoPlatform.Zoom;

  const { delegator: localVideoDelegator, status: localVideoStatus } =
    useAtomValue(localVideoContextAtom);
  const { delegator: localAudioDelegator, status: localAudioStatus } =
    useAtomValue(localAudioContextAtom);
  const isVideoEnabled =
    localVideoDelegator && localVideoStatus === LocalMediaStatus.READY;
  const isAudioEnabled =
    localAudioDelegator && localAudioStatus === LocalMediaStatus.READY;

  return (
    <AudioVideoTroubleshootActionItem
      {...props}
      permissionIsBlocked={videoIsBlocked || microphoneIsBlocked}
      isVideoEnabled={isVideoEnabled}
      isAudioEnabled={isAudioEnabled}
      shouldShowVideoQualitySettings={!videoPlatformIsZoom}
    />
  );
}
